import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { CookDetails, MeatProperties } from '../../types';
import NumberInput from '../NumberInput';

interface Props {
  data: Partial<CookDetails>;
  onChange: (data: Partial<CookDetails>) => void;
  onNext: () => void;
}

export default function WeightInput({ data, onChange }: Props) {
  const pieces = data.pieces || [{ id: 'A', weight: 0 }];
  const [meatConfig, setMeatConfig] = useState<MeatProperties | null>(null);

  // Load meat config for beef ribs
  useEffect(() => {
    if (data.meatType === 'beef-ribs') {
      fetch('/config/meats.json')
        .then(res => res.json())
        .then((meats: MeatProperties[]) => {
          const meat = meats.find(m => m.type === 'beef-ribs');
          if (meat) setMeatConfig(meat);
        })
        .catch(err => console.error('Failed to load meat config:', err));
    } else {
      setMeatConfig(null);
    }
  }, [data.meatType]);

  const updatePiece = (index: number, weight: number) => {
    const updated = [...pieces];
    updated[index] = { ...updated[index], weight };
    onChange({ pieces: updated });
  };

  const addPiece = () => {
    const nextId = String.fromCharCode(65 + pieces.length); // A, B, C, etc.
    onChange({ pieces: [...pieces, { id: nextId, weight: 0 }] });
  };

  const removePiece = (index: number) => {
    if (pieces.length > 1) {
      onChange({ pieces: pieces.filter((_, i) => i !== index) });
    }
  };

  const totalWeight = pieces.reduce((sum, p) => sum + p.weight, 0);
  const isBeefRibs = data.meatType === 'beef-ribs';
  const recommendedTemp = meatConfig?.empirical?.recommendedSmokerTempF || 250;
  const cookTimeAtTemp = meatConfig?.empirical?.fixedBaseTimeAt250F || 9;

  return (
    <div className="space-y-4">
      {/* Beef ribs info callout */}
      {isBeefRibs && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>ℹ️ Beef ribs cook by thickness (~{cookTimeAtTemp} hours at {recommendedTemp}°F), not weight.</strong><br />
            Enter approximate weights — cook time is based on thickness, so weight only affects
            thermal load adjustments when cooking multiple pieces.
          </p>
          <p className="text-xs text-blue-700 mt-2">
            Typical: Short ribs 1-2 lbs each, Plate ribs 2.5-3.5 lbs each
          </p>
        </div>
      )}
      
      {/* Pieces */}
      <div className="space-y-3">
        {pieces.map((piece, index) => (
          <div key={piece.id} className="flex gap-3 items-center">
            <div className="w-10 h-10 rounded-lg bg-flame-100 text-flame-700 font-bold flex items-center justify-center flex-none">
              {piece.id}
            </div>
            <NumberInput
              value={piece.weight || undefined}
              onChange={(val) => updatePiece(index, val ?? 0)}
              min={0.1}
              max={100}
              step={0.1}
              placeholder="lbs"
              decimalPlaces={1}
              className="flex-1"
            />
            {pieces.length > 1 && (
              <button
                onClick={() => removePiece(index)}
                aria-label={`Remove piece ${piece.id}`}
                className="p-2 text-smoke-400 hover:text-flame-600 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add Piece Button */}
      <button
        onClick={addPiece}
        className="w-full py-3 border-2 border-dashed border-smoke-300 rounded-lg text-smoke-600 hover:border-flame-500 hover:text-flame-600 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        Add Another Piece
      </button>

      {/* Total */}
      <div className="p-4 bg-smoke-100 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-smoke-900">Total Weight</span>
          <span className="text-2xl font-bold text-flame-600">{totalWeight.toFixed(1)} lbs</span>
        </div>
      </div>
    </div>
  );
}
